import { useMemo } from "react";
import { RIM_STYLES } from "./car/paint.js";
import { dimsOf } from "./dims.js";
import { peakPowerKw, zeroTo100 } from "./drive/perf.js";
import { tuneOf } from "./drive/tune.js";
import { useVehicleT } from "./i18n.js";
import { TopUpButton } from "./TopUp.js";
import { useVehicles, vehicleStore } from "./store.js";
import type { VehicleModel } from "./types.js";

const fmt = (n: number): string => Math.round(n).toLocaleString("en-US").replace(/,/g, ".");

/** The Galeri panel: a drawer (a bottom sheet on phones) with the stock, colours, buy and "my vehicles". */
export function GaleriPanel() {
  const t = useVehicleT();
  const open = useVehicles((s) => s.galeri.open);
  const tab = useVehicles((s) => s.galeri.tab);
  const credits = useVehicles((s) => s.credits);
  if (!open) return null;
  return (
    <aside className="veh-panel" data-testid="galeri-panel" role="dialog" aria-label={t("galeri")}>
      <header className="veh-panel-head">
        <h2>{t("galeri")}</h2>
        <span className="veh-wallet" data-testid="galeri-wallet" title={t("wallet")}>
          {credits === null ? "–" : t("credits", { n: fmt(credits) })}
        </span>
        <TopUpButton />
        <button
          type="button"
          className="veh-x"
          data-testid="galeri-close"
          aria-label={t("close")}
          onClick={() => vehicleStore.closeGaleri()}
        >
          ×
        </button>
      </header>
      <div className="veh-tabs" role="tablist">
        {(["shop", "mine"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            data-testid={`galeri-tab-${k}`}
            onClick={() => vehicleStore.setTab(k)}
          >
            {k === "shop" ? t("tabShop") : t("tabMine")}
          </button>
        ))}
      </div>
      {tab === "shop" ? <Shop /> : <Mine />}
    </aside>
  );
}

function Bar({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="veh-bar" data-testid={testId}>
      <span>{label}</span>
      <i>
        <b style={{ width: `${Math.round(Math.max(0.04, Math.min(1, value)) * 100)}%` }} />
      </i>
    </div>
  );
}

const accelRating = (m: VehicleModel): number => (m.accel <= 1 ? m.accel : m.accel / 14);

function Shop() {
  const t = useVehicleT();
  const catalog = useVehicles((s) => s.catalog);
  const preview = useVehicles((s) => s.preview);
  const credits = useVehicles((s) => s.credits);
  const busy = useVehicles((s) => s.busy);
  const error = useVehicles((s) => s.error);
  const lastPurchase = useVehicles((s) => s.lastPurchase);
  const confirm = useVehicles((s) => s.confirm);
  const delivering = useVehicles((s) => s.delivering);
  const mine = useVehicles((s) => s.mine);
  const maxSpeed = useMemo(() => Math.max(1, ...catalog.map((m) => m.topSpeedKmh)), [catalog]);
  const model = catalog.find((m) => m.id === preview.modelId) ?? catalog[0];
  const bought = lastPurchase ? mine.find((v) => v.id === lastPurchase) : undefined;
  const boughtModel = bought ? catalog.find((m) => m.id === bought.modelId) : undefined;
  if (!model) return <p className="veh-empty">…</p>;
  const color = preview.color ?? model.colors[0]!;
  const afford = credits === null || credits >= model.priceCredits;
  const d = dimsOf(model);
  const tune = tuneOf(d.class, d, model);
  const z100 = zeroTo100(d, tune);
  const kw = peakPowerKw(tune);
  return (
    <div className="veh-body">
      <ul className="veh-list" data-testid="galeri-list">
        {catalog.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              data-testid={`galeri-model-${m.id}`}
              aria-pressed={m.id === model.id}
              onClick={() => vehicleStore.preview(m.id)}
            >
              <span className="veh-name">{m.name}</span>
              <span className="veh-class">{t(`class.${m.class}`, { defaultValue: m.class })}</span>
              <span className="veh-price">{t("credits", { n: fmt(m.priceCredits) })}</span>
            </button>
          </li>
        ))}
      </ul>
      <section className="veh-detail" aria-live="polite">
        <h3>{model.name}</h3>
        <p className="veh-sub">
          {t(`class.${model.class}`, { defaultValue: model.class })} ·{" "}
          {t("seats", { n: model.seats })}
        </p>
        <Bar label={t("speed")} value={model.topSpeedKmh / maxSpeed} testId="galeri-bar-speed" />
        <Bar label={t("accel")} value={accelRating(model)} testId="galeri-bar-accel" />
        <dl className="veh-specs" data-testid="galeri-specs" aria-label={t("specs")}>
          <div>
            <dt>{t("zeroTo100")}</dt>
            <dd data-testid="spec-0-100">
              {Number.isFinite(z100) ? t("seconds", { n: z100.toFixed(1) }) : "–"}
            </dd>
          </div>
          <div>
            <dt>{t("speed")}</dt>
            <dd>
              {Math.round(model.topSpeedKmh)} {t("kmh")}
            </dd>
          </div>
          <div>
            <dt>{t("seatsLabel")}</dt>
            <dd>{model.seats}</dd>
          </div>
          <div>
            <dt>{t("drivetrain")}</dt>
            <dd>{t(`drive_${tune.drive}`)}</dd>
          </div>
          <div>
            <dt>kW</dt>
            <dd>{kw}</dd>
          </div>
          <div>
            <dt>{t("price")}</dt>
            <dd>{fmt(model.priceCredits)}</dd>
          </div>
        </dl>
        <div className="veh-colors" role="radiogroup" aria-label={t("color")}>
          {model.colors.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={c === color}
              aria-label={t("colorName", { hex: c })}
              data-testid={`galeri-color-${c.slice(1)}`}
              className="veh-swatch veh-swatch-gloss"
              style={{ background: c }}
              onClick={() => vehicleStore.setPreviewColor(c)}
            />
          ))}
        </div>
        <div className="veh-rims" role="radiogroup" aria-label={t("rims")}>
          {RIM_STYLES.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={preview.rim === r}
              data-testid={`galeri-rim-${r}`}
              data-rim={r}
              onClick={() => vehicleStore.setPreviewRim(r)}
            >
              {t(`rim.${r}`)}
            </button>
          ))}
        </div>
        <p className="veh-hint">{t("orbitHint")}</p>
        <div className="veh-cta">
          <button
            type="button"
            className="veh-test"
            data-testid="galeri-test-drive"
            disabled={busy}
            onClick={() => vehicleStore.testDrive(model.id, color)}
          >
            {t("testDrive")}
          </button>
          <button
            type="button"
            className="veh-buy"
            data-testid="galeri-buy"
            disabled={busy || !afford}
            onClick={() => vehicleStore.askBuy(model.id)}
          >
            {busy ? t("buying") : `${t("buy")} · ${t("credits", { n: fmt(model.priceCredits) })}`}
          </button>
        </div>
        {confirm === model.id ? (
          <div className="veh-confirm" role="alertdialog" data-testid="galeri-confirm">
            <p>{t("confirmBuy", { name: model.name, price: fmt(model.priceCredits) })}</p>
            <div>
              <button
                type="button"
                className="veh-buy"
                data-testid="galeri-confirm-yes"
                disabled={busy}
                onClick={() => void vehicleStore.buy(model.id, color)}
              >
                {t("confirmYes")}
              </button>
              <button
                type="button"
                data-testid="galeri-confirm-no"
                onClick={() => vehicleStore.cancelBuy()}
              >
                {t("confirmNo")}
              </button>
            </div>
          </div>
        ) : null}
        {delivering && performance.now() - delivering.at < 4000 ? (
          <p className="veh-note" role="status">
            {t("delivering")}
          </p>
        ) : null}
        {!afford ? <p className="veh-note veh-bad">{t("insufficient")}</p> : null}
        {error ? (
          <p className="veh-note veh-bad" role="alert" data-testid="galeri-error">
            {t(error)}
          </p>
        ) : null}
        {boughtModel ? (
          <p className="veh-note veh-good" role="status" data-testid="galeri-bought">
            {t("bought", { name: boughtModel.name })}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function Mine() {
  const t = useVehicleT();
  const mine = useVehicles((s) => s.mine);
  const catalog = useVehicles((s) => s.catalog);
  const busy = useVehicles((s) => s.busy);
  if (mine.length === 0) return <p className="veh-empty">{t("noneOwned")}</p>;
  return (
    <ul className="veh-mine" data-testid="galeri-mine">
      {mine.map((v) => {
        const m = catalog.find((x) => x.id === v.modelId);
        const resale = Math.round((m?.priceCredits ?? 0) * 0.6);
        return (
          <li key={v.id} data-testid="galeri-mine-item" data-vehicle={v.id}>
            <div className="veh-mine-head">
              <i className="veh-swatch" style={{ background: v.color }} />
              <strong>{m?.name ?? v.modelId}</strong>
              <span className="veh-sub">
                {v.state === "driving" ? t("driving") : t("parked")} ·{" "}
                {t("odometer", { km: (v.odometerM / 1000).toFixed(1) })}
              </span>
            </div>
            <div className="veh-actions">
              <button
                type="button"
                data-testid="galeri-locate"
                onClick={() => vehicleStore.locate(v.id)}
              >
                {t("locate")}
              </button>
              <button
                type="button"
                data-testid="galeri-drive"
                disabled={busy || v.state === "driving"}
                onClick={() => void vehicleStore.enter(v.id)}
              >
                {t("drive")}
              </button>
              <button
                type="button"
                data-testid="galeri-sell"
                disabled={busy || v.state === "driving"}
                onClick={() => void vehicleStore.sell(v.id)}
              >
                {t("sellFor", { n: fmt(resale) })}
              </button>
            </div>
            <div className="veh-colors" aria-label={t("paint")}>
              {(m?.colors ?? []).map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={t("colorName", { hex: c })}
                  data-testid={`galeri-paint-${c.slice(1)}`}
                  className="veh-swatch veh-swatch-sm"
                  aria-pressed={c === v.color}
                  style={{ background: c }}
                  disabled={busy}
                  onClick={() => void vehicleStore.paint(v.id, c)}
                />
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
